import { useState, useEffect, useCallback } from "react";
import { adminApi } from "../../api";

/**
 * Billing modal logic — fetches docs on open, allows emit FT/RB/NC + recalc commissions.
 */
export default function useBillingModal() {
  const [order, setOrder] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(false); // false | 'FT' | 'RB' | 'NC' | 'CALC'
  const [message, setMessage] = useState(null);

  const open = useCallback((o) => setOrder(o), []);
  const close = useCallback(() => {
    setOrder(null); setData(null); setMessage(null); setEmitting(false);
  }, []);

  useEffect(() => {
    if (!order) return undefined;
    let cancelled = false;
    setLoading(true); setMessage(null);
    adminApi.orderBilling(order.ID_Encomenda)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ ok: false, facturas: [], recibos: [], notas_credito: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [order]);

  const emitDoc = useCallback(async (tipo) => {
    if (!order) return;
    setEmitting(tipo); setMessage(null);
    try {
      const r = await adminApi.orderEmitDocument({ order_id: order.ID_Encomenda, tipo });
      setMessage(r);
      const fresh = await adminApi.orderBilling(order.ID_Encomenda);
      setData(fresh);
    } catch (e) {
      setMessage({ success: false, message: e.message || "Erro ao gerar." });
    } finally {
      setEmitting(false);
    }
  }, [order]);

  const recalcCommissions = useCallback(async () => {
    if (!order) return;
    setEmitting("CALC"); setMessage(null);
    try {
      const r = await adminApi.orderRecalcCommissions({ order_id: order.ID_Encomenda });
      setMessage(r);
    } catch (e) {
      setMessage({ success: false, message: e.message || "Erro ao recalcular." });
    } finally {
      setEmitting(false);
    }
  }, [order]);

  const resendPrEmail = useCallback(async () => {
    if (!order) return;
    setEmitting("EMAIL_PR"); setMessage(null);
    try {
      const r = await adminApi.orderResendPrEmail(order.ID_Encomenda);
      setMessage({ success: true, message: r?.message || "Email 'Pagamento Recebido' reenviado." });
    } catch (e) {
      setMessage({ success: false, message: e.message || "Erro ao reenviar email." });
    } finally {
      setEmitting(false);
    }
  }, [order]);

  const refreshCofidisLink = useCallback(async () => {
    if (!order) return;
    setEmitting("COFIDIS"); setMessage(null);
    try {
      const r = await adminApi.orderRefreshCofidisLink(order.ID_Encomenda, true);
      setMessage({ success: true, message: r?.message || "Novo link Cofidis gerado.", extra: r?.url });
    } catch (e) {
      setMessage({ success: false, message: e.message || "Erro ao regenerar link Cofidis." });
    } finally {
      setEmitting(false);
    }
  }, [order]);

  return {
    order, data, loading, emitting, message,
    open, close, emitDoc, recalcCommissions, resendPrEmail, refreshCofidisLink,
  };
}
